import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Typography, Card, Input } from 'antd';
// Types
import { GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items } from 'api/campaignV2/types/GetCampaignOffers';
import {
  CbDeliverableItemV2Status,
  DeliverableReviewAction,
} from 'api/graphql-global-types';
// Components
import {
  ALREADY_REVIEWED_STATUSES,
  DeliverableReviewData,
} from '../../TimelineDeliverablesReviewForm';
import ReviewButtons from '../ReviewButtons/ReviewButtons';
// Styles
import styles from './CaptionReviewSection.module.scss';

const { Text, Title } = Typography;
const { TextArea } = Input;

type CaptionReviewSectionProps = {
  captionItems: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items[];
  onRejectClick?: (
    item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
  ) => void;
};

const CaptionReviewSection: React.FC<CaptionReviewSectionProps> = ({
  captionItems,
  onRejectClick,
}) => {
  const methods = useFormContext<DeliverableReviewData>();

  const handleReview = (itemId: string, action: DeliverableReviewAction) => {
    const currentReview = methods.watch('postReview');

    // Toggle approval - if already approved, remove action
    if (currentReview.action === action) {
      methods.setValue('postReview', {
        ...currentReview,
        action: null,
        feedback: '',
      });
    } else {
      methods.setValue('postReview', {
        deliverableItemId: itemId,
        action: action,
      });
      methods.clearErrors('postReview');
    }
  };

  const getItemAction = (itemId: string) => {
    const review = methods.getValues('postReview');
    return review.deliverableItemId === itemId ? review.action : null;
  };

  const renderCaptionItem = (
    item: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2_items
  ) => {
    const shouldShow = item.status !== CbDeliverableItemV2Status.NOT_SUBMITTED;
    const alreadyReviewed = ALREADY_REVIEWED_STATUSES.includes(item.status);

    if (!shouldShow) return null;

    const isApproved =
      getItemAction(item.id) === DeliverableReviewAction.APPROVE;
    const isRejected =
      getItemAction(item.id) === DeliverableReviewAction.REJECT;

    return (
      <Card key={item.id} className={styles.captionCard}>
        <div className={styles.captionHeader}>
          <Title level={5} className={styles.captionTitle}>
            {item.task?.title || 'Post Caption'}
          </Title>
          <Text type="secondary" className={styles.statusText}>
            Status: {item.status}
          </Text>
        </div>

        <div className={styles.captionContent}>
          {'copy' in item && (
            <div className={styles.copyTextBox}>
              <TextArea
                value={item.copy || ''}
                rows={5}
                className={styles.copyTextarea}
                readOnly
              />
            </div>
          )}
        </div>

        <div className={styles.actionButtons}>
          <ReviewButtons
            isApproved={isApproved}
            isRejected={isRejected}
            handleApprove={() =>
              !alreadyReviewed &&
              handleReview(item.id, DeliverableReviewAction.APPROVE)
            }
            handleReject={() =>
              !alreadyReviewed &&
              handleReview(item.id, DeliverableReviewAction.REJECT)
            }
            alreadyReviewed={alreadyReviewed}
            onRejectClick={() => onRejectClick?.(item)}
            item={item}
          />
        </div>
      </Card>
    );
  };

  return (
    <div className={styles.captionSection}>
      <div className={styles.sectionHeader}>
        <Title level={4}>Post Caption</Title>
        <Text type="secondary">
          {
            captionItems.filter(
              (item) => item.status !== CbDeliverableItemV2Status.NOT_SUBMITTED
            ).length
          }{' '}
          items
        </Text>
      </div>

      <div className={styles.captionGrid}>
        {captionItems.length > 0 ? (
          captionItems.map(renderCaptionItem)
        ) : (
          <div className={styles.noCaption}>
            <Text type="secondary">No post captions available</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaptionReviewSection;
